import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { newId } from '@familyhub/shared';
import { adb } from './db';
import { savePerson, deletePerson } from './actions';

/**
 * External people without accounts (spec §5): grandparents, a babysitter,
 * daycare. They can be assigned to childcare like a parent, but never sign
 * in. They are only ever caregivers, so there is no type to choose.
 */
export function PeopleScreen() {
  const people = useLiveQuery(() => adb.people.toArray(), [], []);
  const live = (people ?? []).filter((p) => !p.deleted_at);

  /**
   * How many live childcare windows name each person.
   *
   * Removing a caregiver used to be immediate and silent, and it does not only
   * affect the future: `WeekScreen` resolves a caregiver's name through a map
   * built from the people table and falls back to `'?'`, so deleting Oma
   * rewrites every window she was ever down for — including ones already
   * past — to read "bij ?". Nothing said the person was in use and there is no
   * undo. So the count is shown, and a person still referenced asks first.
   */
  const childcare = useLiveQuery(() => adb.childcare.toArray(), [], []);
  const usageOf = (personId: string) =>
    (childcare ?? []).filter(
      (c) => !c.deleted_at && (c.caregivers ?? []).some((g) => g.kind === 'person' && g.id === personId),
    ).length;

  const [name, setName] = useState('');
  /** The person a second tap on "Verwijderen" would remove. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const add = () => {
    if (!name.trim()) return;
    savePerson({ id: newId(), name: name.trim(), kind: 'caregiver', color: null });
    setName('');
  };

  const remove = (personId: string) => {
    if (usageOf(personId) > 0 && confirming !== personId) {
      setConfirming(personId);
      return;
    }
    setConfirming(null);
    deletePerson(personId);
  };

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/agenda" aria-label="Terug naar de agenda">‹ Agenda</Link>
        <h1>Oppas</h1>
      </div>

      <main>
        <div className="agenda-add-person">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Naam" aria-label="Naam" />
          <button className="btn btn-primary" onClick={add} disabled={!name.trim()}>Toevoegen</button>
        </div>

        {live.length === 0 ? (
          <p className="empty">Nog niemand. Voeg oma, oppas of kdv toe om aan opvang te koppelen.</p>
        ) : (
          <div className="card" style={{ marginTop: 16 }}>
            {live.map((p) => {
              const used = usageOf(p.id);
              const asking = confirming === p.id;
              return (
                <div className="row" key={p.id}>
                  <span className="row-main">
                    <span className="row-name">{p.name}</span>
                    {/* What removing them would cost, before it is removed. */}
                    {used > 0 && (
                      <span className="row-sub">
                        {used === 1 ? 'staat 1 keer op oppas' : `staat ${used} keer op oppas`}
                      </span>
                    )}
                    {asking && (
                      <span className="row-sub agenda-coverline warn">
                        {used === 1 ? 'Die opvang komt' : `Die ${used} opvangmomenten komen`} dan zonder naam te
                        staan. Tik nog eens.
                      </span>
                    )}
                  </span>
                  <button
                    className="btn btn-danger"
                    onClick={() => remove(p.id)}
                    aria-label={asking ? `${p.name} definitief verwijderen` : `${p.name} verwijderen`}
                  >
                    {asking ? 'Toch weg' : 'Verwijderen'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
